(() => {
  if (customElements.get('solyra-hero-media-carousel')) return;

  class SolyraHeroMediaCarousel extends HTMLElement {
    connectedCallback() {
      this.slides = Array.from(this.querySelectorAll('[data-hero-media-slide]'));
      this.dots = Array.from(this.querySelectorAll('[data-hero-media-dot]'));
      this.autoplay = this.dataset.autoplay === 'true';
      this.interval = Number.parseInt(this.dataset.interval, 10) || 6000;
      this.currentIndex = this.slides.findIndex((slide) => slide.classList.contains('is-active'));
      this.onBlockSelect = this.handleBlockSelect.bind(this);
      this.mobileMediaQuery = window.matchMedia('(max-width: 749px)');
      this.onViewportChange = this.handleViewportChange.bind(this);
      this.autoplayRunning = false;
      this.activeVideo = null;
      this.onVideoEnded = null;
      this.onVideoError = null;

      if (this.currentIndex < 0) this.currentIndex = 0;

      window.requestAnimationFrame(() => {
        this.classList.add('is-content-timing-ready');
      });

      this.addEventListener('shopify:block:select', this.onBlockSelect);
      this.mobileMediaQuery.addEventListener?.('change', this.onViewportChange);

      if (this.slides.length <= 1) return;

      this.dots.forEach((dot, index) => {
        dot.addEventListener('click', () => this.goTo(index));
      });

      this.startAutoplay();
    }

    disconnectedCallback() {
      this.stopAutoplay();
      this.removeEventListener('shopify:block:select', this.onBlockSelect);
      this.mobileMediaQuery?.removeEventListener?.('change', this.onViewportChange);
    }

    prefersReducedMotion() {
      return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    }

    handleBlockSelect(event) {
      const selectedSlide = event.target.closest('[data-hero-media-slide]');
      const selectedIndex = this.slides.indexOf(selectedSlide);

      if (selectedIndex < 0) return;

      this.stopAutoplay();
      this.goTo(selectedIndex);
    }

    handleViewportChange() {
      if (!this.autoplayRunning) return;
      this.startAutoplay();
    }

    getSlideVideo(slide) {
      if (!slide) return null;

      const desktopVideo = slide.querySelector('.homepage-hero__video--desktop');
      const mobileVideo = slide.querySelector('.homepage-hero__video--mobile');

      if (this.mobileMediaQuery.matches && mobileVideo) return mobileVideo;
      return desktopVideo;
    }

    startSlideVideo(slide) {
      const selectedVideo = this.getSlideVideo(slide);

      this.querySelectorAll('.homepage-hero__video').forEach((video) => {
        if (video === selectedVideo) return;
        video.pause();
      });

      if (!selectedVideo) return null;

      selectedVideo.pause();
      selectedVideo.loop = false;

      try {
        selectedVideo.currentTime = 0;
      } catch (error) {
        // The fallback timer below still advances the carousel if seeking is unavailable.
      }

      return selectedVideo;
    }

    goTo(index) {
      const nextIndex = (index + this.slides.length) % this.slides.length;
      if (nextIndex === this.currentIndex) return;

      this.clearAutoplaySchedule();
      const nextVideo = this.autoplay ? this.startSlideVideo(this.slides[nextIndex]) : null;

      this.slides[this.currentIndex]?.classList.remove('is-active');
      this.slides[this.currentIndex]?.setAttribute('aria-hidden', 'true');
      this.dots[this.currentIndex]?.setAttribute('aria-current', 'false');
      this.dots[this.currentIndex]?.setAttribute('aria-selected', 'false');

      this.currentIndex = nextIndex;

      this.slides[this.currentIndex]?.classList.add('is-active');
      this.slides[this.currentIndex]?.setAttribute('aria-hidden', 'false');
      this.dots[this.currentIndex]?.setAttribute('aria-current', 'true');
      this.dots[this.currentIndex]?.setAttribute('aria-selected', 'true');
      this.restartSlideMotion(this.slides[this.currentIndex]);

      if (this.autoplayRunning) this.scheduleCurrentSlide(nextVideo);
    }

    restartSlideMotion(slide) {
      if (!slide || this.prefersReducedMotion()) return;

      const animatedImage = slide.querySelector('.homepage-hero__image');
      if (!animatedImage) return;

      animatedImage.style.animation = 'none';
      animatedImage.offsetHeight;
      animatedImage.style.animation = '';
    }

    startAutoplay() {
      if (!this.autoplay || this.prefersReducedMotion()) return;
      this.clearAutoplaySchedule();
      this.autoplayRunning = true;
      const activeVideo = this.startSlideVideo(this.slides[this.currentIndex]);
      this.scheduleCurrentSlide(activeVideo);
    }

    scheduleCurrentSlide(video = this.getSlideVideo(this.slides[this.currentIndex])) {
      if (!this.autoplayRunning) return;

      this.clearAutoplaySchedule();

      if (!video) {
        this.timer = window.setTimeout(() => this.goTo(this.currentIndex + 1), this.interval);
        return;
      }

      this.activeVideo = video;
      this.onVideoEnded = () => this.goTo(this.currentIndex + 1);
      this.onVideoError = () => this.scheduleFallback();
      video.addEventListener('ended', this.onVideoEnded, { once: true });
      video.addEventListener('error', this.onVideoError, { once: true });

      const playPromise = video.play();
      if (playPromise) {
        playPromise.catch(() => {
          if (this.activeVideo === video) this.scheduleFallback();
        });
      }
    }

    scheduleFallback() {
      if (!this.autoplayRunning) return;
      this.clearAutoplaySchedule();
      this.timer = window.setTimeout(() => this.goTo(this.currentIndex + 1), this.interval);
    }

    clearAutoplaySchedule() {
      if (this.timer) {
        window.clearTimeout(this.timer);
        this.timer = null;
      }

      if (this.activeVideo) {
        if (this.onVideoEnded) this.activeVideo.removeEventListener('ended', this.onVideoEnded);
        if (this.onVideoError) this.activeVideo.removeEventListener('error', this.onVideoError);
      }

      this.activeVideo = null;
      this.onVideoEnded = null;
      this.onVideoError = null;
    }

    stopAutoplay() {
      this.autoplayRunning = false;
      this.clearAutoplaySchedule();
    }
  }

  customElements.define('solyra-hero-media-carousel', SolyraHeroMediaCarousel);
})();
